import React, { Component, createRef, useEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import lodash, { isError } from 'lodash';

import Tabs from '../../global/Tabs/Tabs';

import PollIcon from '../../Icons/PollIcon';
import EventIcon from '../../Icons/EventIcon';
import NoticeIcon from '../../Icons/NoticeIcon';
import HighFiveIcon from '../../Icons/HighFiveIcon';

import CreateFeedItemSearchGroup from './CreateFeedItemSearchGroup';
import CreateFeedItemSelectedGroup from './CreateFeedItemSelectedGroup';
import CreateFeedItemNotice from './CreateFeedItemNotice';
import CreateFeedItemPoll from './CreateFeedItemPoll';
import CreateFeedItemHighFive from './CreateFeedItemHighFive';
import CreateFeedItemEvent from './CreateFeedItemEvent';
import { logFirebaseEvent } from '../../../firebase/analytics';
import { getMyGroupsRequest } from '../../../actionCreators/groupActions';

import {
  createNoticeRequest,
  createPollRequest,
  createHighFiveRequest,
  createEventRequest,
} from '../../../actionCreators/feedActions';
import TextField from '../../global/fields/TextField';
import { addAlertToAlertsList } from '../../../actionCreators/globalAlerts';

const CreateFeedItem = (props) => {
  const [state, setState] = useState({
    activeTab: 'Notice',
    isFocused: props.isFocused || false,
    selectedGroupId: props.selectedGroupId || '',
    searchGroupValue: '',
    showErrors: false,
  });
  let tabs = [
    { label: 'Notice', icon: NoticeIcon },
    { label: 'Poll', icon: PollIcon },
    { label: 'High Five', icon: HighFiveIcon },
    { label: 'Event', icon: EventIcon },
  ];

  if (props.action === 'share') {
    tabs = [{ label: props.title }];
  }

  useEffect(() => {
    if (props.myGroups) {
      props.getMyGroups();
    }
  }, []);



  const _activateTab = (target) => {
    if (state.activeTab === target) return;
    const newState = { ...state, activeTab: target}
    setState(newState);

    onInputFocus(newState);
  };

  const onInputFocus = (stateData = state) => {
    if (stateData.isFocused) return;

    setState({ ...stateData, isFocused: true });
    if (typeof props.onToggleActive === 'function') props.onToggleActive(true);
  };

  const _close = () => {
    setState({
      ...state,
      isFocused: false,
      selectedGroupId: props.selectedGroupId || '',
      searchGroupValue: '',
      showErrors: false,
    });
    if (typeof props.onToggleActive === 'function') props.onToggleActive(false);
  };

  const onSelect = (selectedGroupId) => {
    setState({ ...state, selectedGroupId: selectedGroupId });
  };

  const onSubmit = async (type, data) => {
    if (!state.selectedGroupId) return setState({ ...state, showErrors: true });
    // Create Notice
    if (type === 'Notice') {
      if (data.notice.error) return setState({ ...state, showErrors: true });
      data.notice = data.notice.value;
      data.group_id = state.selectedGroupId;
      if (data.video) {
        const abortController = new AbortController();
        const onClickCancelVideo = () => {
          abortController.abort();
        };
        const signal = abortController.signal;
        const onFailToCreateNotice = (errMsg) =>
          props.addAlertToAlerts({
            title: 'Message',
            message: errMsg,
            confirmBtnText: 'Okay',
          });
        props.addAlertToAlerts({
          title: 'Message',
          message: 'Uploading Video',
          confirmBtnText: 'Cancel',
          isVideoAlert: true,
          noticeParams: {
            notice: data,
            createNotice: props.createNotice,
            closeNotice: _close,
            signal,
            onFailToCreateNotice,
          },
          successCb: onClickCancelVideo,
        });
      } else {
        props.createNotice({ data, onSuccess: _close });
      }

      let event = 'notice';
      let action = 'create';

      if (data.challenge_id) {
        event = 'challenge';
        action = 'share';
      }

      if (data.knowledgebase_item_id) {
        event = 'knowledgebase';
        action = 'share';
      }

      logFirebaseEvent({
        event: event,
        action: action,
        attachment: data.video ? 'video' : data.image ? 'image' : '',
      });
    }

    // Create Poll
    if (type === 'Poll') {
      if (data.question.error || data.options.some((option) => option.error) || !data.duration) {
        return setState({ ...state, showErrors: true });
      }

      props.createPoll({
        group_id: state.selectedGroupId,
        question: data.question.value,
        options: data.options.map((option) => option.value),
        duration: data.duration,
      });
      logFirebaseEvent({
        event: 'poll',
        action: 'create',
      });
      return _close();
    }

    // Create High Five
    if (type === 'High Five') {
      if (!data.recipient_id || !data.highfive_type_id || (data.reason && data.reason.error)) {
        return setState({ ...state, showErrors: true });
      }

      if (data.reason) data.reason = data.reason.value;
      data.group_id = state.selectedGroupId;
      props.createHighFive(data);
      logFirebaseEvent({
        event: 'highfive',
        action: 'create',
      });
      return _close();
    }

    if (type === 'Event') {
      logFirebaseEvent({
        event: 'events',
        action: 'create',
      });
      if (
        data.title.error ||
        data.description.error ||
        data.location.error ||
        data.start_time.error ||
        (data.end_time && data.end_time.error)
      ) {
        return setState({ ...state, showErrors: true });
      }

      data.title = data.title.value;
      data.description = data.description.value;
      data.location = data.location.value;
      data.start_time = data.start_time.value;
      if (data.end_time) data.end_time = data.end_time.value;

      data.group_id = state.selectedGroupId;
      props.createEvent(data);

      return _close();
    }
  };

  const checkFields = (type, data) => {    
    if (!state.selectedGroupId) return false;
    // Create Notice
    if (type === 'Notice') {
      if (!data.notice.value || data.notice.error || state.loading) return false;
    }

    // Create Poll
    if (type === 'Poll') {
      if (
        !data.question.value ||
        data.question.error ||
        data.options.some((option) => option.error) ||
        !data.duration
      ) {
        return false;
      }
    }

    // Create High Five
    if (type === 'High Five') {
      if (!data.recipient_id || !data.highfive_type_id) {
        return false;
      }
    }

    if (type === 'Event') {
      if (
        data.title.error ||
        data.description.error ||
        data.location.error ||
        data.start_time.error ||
        (data.end_time && data.end_time.error)
      ) {
        return false;
      }
    }

    return true;
  };

  const renderGroupRow = () => {
    if (!state.isFocused && props.selectedGroupId) {
      return (
        <div className="create-feed-content-input-wrapper">
          <TextField
            fk="useless"
            value=""
            label="New post"
            updateFieldData={() => {}}
            onFocus={onInputFocus}
          />
        </div>
      );
    }

    if (state.selectedGroupId && state.activeTab != 'Notice' && !props.isAdmin) {
      const selectedGroup = lodash.find(props.myGroups, { id: state.selectedGroupId });
      if (!selectedGroup.is_admin) {
        setState({ ...state, selectedGroupId: null });
      }
    }

    if (!state.selectedGroupId) {
      return (
        <CreateFeedItemSearchGroup
          onFocus={onInputFocus}
          onSelect={onSelect}
          groups={props.myGroups}
          featureFlags={props.featureFlags}
          activeTab={state.activeTab}
          isActive={state.isFocused}
          showError={state.showErrors}
        />
      );
    }

    const selectedGroup = lodash.find(props.myGroups, { id: state.selectedGroupId });
    if (!selectedGroup) return null;

    return (
      <CreateFeedItemSelectedGroup
        group={selectedGroup}
        onCancel={() => setState({ ...state, selectedGroupId: '' })}
      />
    );
  };

  const renderFocusedContent = () => {
    if (!state.isFocused) return null;
    const selectedGroup = lodash.find(props.myGroups, { id: state.selectedGroupId });
    const {
      challengeItem = false,
      shouldShowAttachedMedia = true,
      isAdmin,
      from,
      knowledgebaseItem = false,
    } = props;

    if (state.activeTab === 'Notice') {
      return (
        <CreateFeedItemNotice
          onSubmit={onSubmit}
          showErrors={state.showErrors}
          checkFields={checkFields}
          isAdmin={isAdmin}
          selectedGroup={selectedGroup}
          shouldShowAttachedMedia={shouldShowAttachedMedia}
          challengeItem={challengeItem}
          knowledgebaseItem={knowledgebaseItem}
        />
      );
    }
    if (state.activeTab === 'Poll') {
      return (
        <CreateFeedItemPoll
          onSubmit={onSubmit}
          showErrors={state.showErrors}
          checkFields={checkFields}
          isAdmin={props.isAdmin}
          selectedGroup={selectedGroup}
        />
      );
    }
    if (state.activeTab === 'High Five') {
      return (
        <CreateFeedItemHighFive
          onSubmit={onSubmit}
          selectedGroupId={state.selectedGroupId}
          showErrors={state.showErrors}
          checkFields={checkFields}
        />
      );
    }
    if (state.activeTab === 'Event') {
      return (
        <CreateFeedItemEvent
          onSubmit={onSubmit}
          showErrors={state.showErrors}
          checkFields={checkFields}
        />
      );
    }

    return null;
  };

  const { isFocused } = state;

  return (
    <div className="create-feed">
      {isFocused && <div className="create-feed-overlay" onClick={_close} />}

      <div
        className={`create-feed-holder ${isFocused ? 'active' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <Tabs tabs={tabs} onChange={_activateTab} active={state.activeTab} />

        <div className={`create-feed-content ${state.isFocused ? '' : 'collapsed'}`}>
          {renderGroupRow()}
          {renderFocusedContent()}
        </div>
      </div>
    </div>
  );
};

const CreateFeedItemContainer = (props) => {
  const state = useSelector((state) => ({
    myGroups: state.group.myGroups,
    isAdmin: state.currentUser.userData && state.currentUser.userData.is_admin ? true : false,
    featureFlags: state.featureFlags,
  }));

  const dispatch = useDispatch();

  const actions = {
    addAlertToAlerts: (payload) => dispatch(addAlertToAlertsList(payload)),
    createNotice: (payload) => dispatch(createNoticeRequest(payload)),
    createPoll: (payload) => dispatch(createPollRequest(payload)),
    createHighFive: (payload) => dispatch(createHighFiveRequest(payload)),
    createEvent: (payload) => dispatch(createEventRequest(payload)),
    getMyGroups: () => dispatch(getMyGroupsRequest()),
  };

  return <CreateFeedItem {...props} {...state} {...actions} />;
};

export default CreateFeedItemContainer;
